import * as zod from 'zod';

import { StepSummary } from './step';

export const RoutineData = zod.object({
  id: zod.string(),
  configurationUpdatedAt: zod.string().nullable(),
  createdAt: zod.string(),
  createdByID: zod.string(),
  createdByName: zod.string().nullable(),
  createdByPhoto: zod.string().nullable(),
  description: zod.string(),
  environmentVariables: zod
    .array(
      zod
        .object({
          id: zod.string(),
          name: zod.string(),
          initialValue: zod.any().optional(),
          // Marks a cached reference to a workspace-global variable, as opposed
          // to a routine-local one. Declared (rather than left to passthrough)
          // because callers branch on it. Deliberately no `.default(false)` —
          // unlike routine-runner's Variable schema — so parsing does not
          // materialize the key on entries that never carried it.
          isGlobal: zod.boolean().optional(),
          persistent: zod.boolean().optional(),
          persistentValue: zod.unknown().optional(),
        })
        .passthrough(),
    )
    .nullable(),
  isTemplate: zod.boolean(),
  name: zod.string(),
  mainLoopStepID: zod.string().nullable(),
  space: zod
    .array(
      zod
        .object({
          description: zod.string(),
          id: zod.string(),
          kind: zod.string(),
          name: zod.string(),
        })
        .passthrough(), // additional props are valid
    )
    .nullable(),
  steps: zod.array(StepSummary),
  stepConfigurations: zod
    .record(
      zod.object({
        args: zod.any().optional(),
        description: zod.string().optional(),
        substepsMinimized: zod.boolean().optional(),
        suppressed: zod.boolean().optional(),
      }),
    )
    .nullable(),
  updatedAt: zod.string(),
  motionPlanner: zod
    .union([zod.literal('ROS1'), zod.literal('ROS2')])
    .nullable(),
  // Server-stamped flags. Clients cannot set these directly: the routines
  // service's `before:create` / `before:patch` hooks strip any
  // client-supplied values and re-stamp from `params.ephemeral`.
  isHidden: zod.boolean().default(false),
  isEphemeral: zod.boolean().default(false),
});

export type RoutineData = zod.infer<typeof RoutineData>;
